import React from 'react'
import { Link } from 'gatsby'
import { LOCALE } from '@foxrenderfarm/internals'
import { LightDropDownRow, LightDropDownTitle, NavDropdownTrigger } from '../../atoms'
import { DownArrowIcon } from '../../svg'

export const Trigger = ({ children }: { children: React.ReactNode }) => (
  <NavDropdownTrigger>
    {children}
    <DownArrowIcon />
  </NavDropdownTrigger>
)

export const withLocale = (to: string, locale: LOCALE) =>
  locale === LOCALE.EN ? to : `/${locale}${to}`

const LOCAL_APP_PORT_PATTERN = /:(8084|8086|8087|9000)$/

export const getLocalAppOrigin = (origin: string, port: number) =>
  origin.replace(LOCAL_APP_PORT_PATTERN, `:${port}`)

export const isPathActive = (pathname: string, url: string, locale: LOCALE) => {
  if (locale === LOCALE.EN) return pathname === url || pathname === `/en${url}`
  return pathname === `/${locale}${url}`
}

type ResourceSectionTitle = 'contentHub' | 'programsPerks'
type SupportSectionTitle = 'guides' | 'account' | 'renderingTutorial'

const RESOURCE_SECTION_TITLES: Record<LOCALE, Record<ResourceSectionTitle, string>> = {
  [LOCALE.EN]: {
    contentHub: 'Content Hub',
    programsPerks: 'Programs & Perks',
  },
  [LOCALE.ES]: {
    contentHub: 'Centro de contenidos',
    programsPerks: 'Programas y ventajas',
  },
  [LOCALE.PT]: {
    contentHub: 'Central de conteúdo',
    programsPerks: 'Programas e benefícios',
  },
  [LOCALE.DE]: {
    contentHub: 'Content Hub',
    programsPerks: 'Programme & Vorteile',
  },
  [LOCALE.TR]: {
    contentHub: 'İçerik merkezi',
    programsPerks: 'Programlar ve avantajlar',
  },
  [LOCALE.JP]: {
    contentHub: 'コンテンツハブ',
    programsPerks: 'プログラム&特典',
  },
  [LOCALE.IT]: {
    contentHub: 'Hub dei contenuti',
    programsPerks: 'Programmi e vantaggi',
  },
  [LOCALE.FR]: {
    contentHub: 'Hub de contenu',
    programsPerks: 'Programmes et avantages',
  },
  [LOCALE.RU]: {
    contentHub: 'Центр контента',
    programsPerks: 'Программы и привилегии',
  },
  [LOCALE.KR]: {
    contentHub: '콘텐츠 허브',
    programsPerks: '프로그램 & 혜택',
  },
}

export const getResourceSectionTitle = (locale: LOCALE, title: ResourceSectionTitle) =>
  RESOURCE_SECTION_TITLES[locale]?.[title] ?? RESOURCE_SECTION_TITLES[LOCALE.EN][title]

const SUPPORT_SECTION_TITLES: Record<LOCALE, Record<SupportSectionTitle, string>> = {
  [LOCALE.EN]: {
    guides: 'Guides',
    account: 'Account',
    renderingTutorial: 'Rendering Tutorial',
  },
  [LOCALE.ES]: {
    guides: 'Guías',
    account: 'Cuenta',
    renderingTutorial: 'Tutorial de renderizado',
  },
  [LOCALE.PT]: {
    guides: 'Guias',
    account: 'Conta',
    renderingTutorial: 'Tutorial de renderização',
  },
  [LOCALE.DE]: {
    guides: 'Leitfäden',
    account: 'Konto',
    renderingTutorial: 'Rendering-Tutorials',
  },
  [LOCALE.TR]: {
    guides: 'Rehberler',
    account: 'Hesap',
    renderingTutorial: 'Render eğitimi',
  },
  [LOCALE.JP]: {
    guides: 'ガイド',
    account: 'アカウント',
    renderingTutorial: 'レンダリングチュートリアル',
  },
  [LOCALE.IT]: {
    guides: 'Guide',
    account: 'Account',
    renderingTutorial: 'Tutorial di rendering',
  },
  [LOCALE.FR]: {
    guides: 'Guides',
    account: 'Compte',
    renderingTutorial: 'Tutoriel de rendu',
  },
  [LOCALE.RU]: {
    guides: 'Руководства',
    account: 'Аккаунт',
    renderingTutorial: 'Руководство по рендерингу',
  },
  [LOCALE.KR]: {
    guides: '가이드',
    account: '계정',
    renderingTutorial: '렌더링 튜토리얼',
  },
}

export const getSupportSectionTitle = (locale: LOCALE, title: SupportSectionTitle) =>
  SUPPORT_SECTION_TITLES[locale]?.[title] ?? SUPPORT_SECTION_TITLES[LOCALE.EN][title]

/** 九个 Solutions 软件落地页（与 solutions-dropdown 保持一致） */
export const SOLUTION_RENDER_FARM_PATHS = [
  '/blender-render-farm.html',
  '/c4d-render-farm.html',
  '/arnold-render-farm.html',
  '/3ds-max-render-farm.html',
  '/houdini-render-farm.html',
  '/vray-render-farm.html',
  '/maya-render-farm.html',
  '/unreal-engine-render-farm.html',
  '/nuke-render-farm.html',
] as const

const normalizePathname = (pathname: string) =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

/** 首页 + 九个 solution 页顶置时可使用透明 header，其余页面始终为实心白底样式 */
export const isTransparentHeaderPath = (pathname: string, locale: LOCALE) => {
  const path = normalizePathname(pathname)
  const homePaths = ['/', locale === LOCALE.EN ? '/en' : `/${locale}`]

  return (
    homePaths.includes(path) ||
    SOLUTION_RENDER_FARM_PATHS.some((p) => isPathActive(pathname, p, locale))
  )
}

interface MainLinkProps {
  to: string
  href?: string
  useGatsbyLink: boolean
  locale: LOCALE
  children: React.ReactNode
  $highlight?: boolean
  className?: string
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

export const MainLink = ({ to, href, useGatsbyLink, locale, children, ...rest }: MainLinkProps) => {
  if (useGatsbyLink) {
    return (
      <LightDropDownRow as={Link} to={to} {...rest}>
        {children}
      </LightDropDownRow>
    )
  }
  return (
    <LightDropDownRow as='a' href={href ?? to} {...rest}>
      {children}
    </LightDropDownRow>
  )
}

interface LightNavDropDownPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  panelTitle?: React.ReactNode
  visible?: boolean
}

export const LightNavDropDownPanel = ({
  panelTitle,
  children,
  visible,
  ...rest
}: LightNavDropDownPanelProps) => {
  if (visible === false) return null
  const { style, ...restProps } = rest
  const forceVisibleStyle: React.CSSProperties = { visibility: 'visible', opacity: 1 }
  const mergedStyle: React.CSSProperties | undefined = visible
    ? { ...(style || {}), ...forceVisibleStyle }
    : style
  return (
    <div className='lightNavDropDown' {...restProps} style={mergedStyle}>
      {panelTitle ? <LightDropDownTitle>{panelTitle}</LightDropDownTitle> : null}
      {children}
    </div>
  )
}
